import { useState, useCallback, useEffect } from 'react';
import { deiterateAPI } from "@/js/external-js/variableModules/utils/deiterateAPI.util";
import { useTenancy } from "@/contexts/TenancyContext";

// Simple interface for the tenancy data we need
interface Tenancy {
  id: string;
  name?: string;
  [key: string]: any;
}

export interface DashboardDataResponse {
  data: {
    [key: string]: {
      data: any;
      metadata?: {
        lastUpdated?: string;
        [key: string]: any;
      };
    };
  };
  metadata?: {
    lastUpdated?: string;
    [key: string]: any;
  };
}

/**
 * Fetches dashboard data for a specific widget
 * @param widgetType The type of widget to fetch data for (e.g., 'upcoming-tasks', 'compliance-health')
 * @param customerId Optional customer ID, will use active tenancy if not provided
 * @returns Promise with the collected data or null if not found
 */
export const getDashboardData = async (
  widgetType: string,
  customerId?: string
): Promise<{ data: any; metadata?: any } | null> => {
  try {
    // If customerId is not provided, try to get it from the tenancy context
    let targetCustomerId = customerId;
    
    if (!targetCustomerId) {
      // This is a React hook, so it can only be used in components
      // Components should pass the customerId directly when possible
      console.warn('No customerId provided to getDashboardData. Using active tenancy.');
      const { activeTenancy } = useTenancy();
      targetCustomerId = activeTenancy?.id;
      
      if (!targetCustomerId || targetCustomerId === 'Loading...') {
        console.error('No customer ID available for dashboard data fetch');
        return null;
      }
    }

    // Make the API call to fetch the dashboard data
    const response = await deiterateAPI.GET<DashboardDataResponse>(
      `/dashboard/data?customerId=${targetCustomerId}&widgetType=${widgetType}`
    );

    // Extract the widget data from the response
    const widgetData = response.data?.[widgetType]?.data;
    const metadata = response.metadata || {};
    
    if (!widgetData) {
      console.warn(`No data found for widget type: ${widgetType}`);
      return { data: null, metadata };
    }
    
    return {
      data: widgetData,
      metadata: {
        ...metadata,
        lastUpdated: widgetData.lastUpdated || metadata.lastUpdated,
      }
    };
  } catch (error: any) {
    console.error(`Error fetching dashboard data for ${widgetType}:`, error);
    
    // Handle 404 specifically
    if (error.response?.status === 404) {
      return {
        data: null,
        metadata: {
          error: 'Data not found',
          collectionTriggered: error.response.data?.collectionTriggered || false,
          message: error.response.data?.message || 'Data not found. Please try again later.',
        }
      };
    }
    
    // Handle other errors
    return {
      data: null,
      metadata: {
        error: 'Failed to load data',
        message: error.message || 'An error occurred while fetching data',
      }
    };
  }
};

/**
 * Hook to fetch dashboard data for a widget
 * @param widgetType The type of widget to fetch data for
 * @returns Object containing data, loading state, and error state
 */
interface UseDashboardDataResult {
  data: any;
  isLoading: boolean;
  error: string | null;
  metadata: Record<string, any>;
  refetch: () => Promise<void>;
}

export const useDashboardData = (widgetType: string): UseDashboardDataResult => {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<Record<string, any>>({});
  const { activeTenancy } = useTenancy();
  
  const fetchData = useCallback(async (): Promise<void> => {
    if (!activeTenancy.id || activeTenancy.id === 'Loading...') {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await getDashboardData(widgetType, activeTenancy.id);
      
      if (result) {
        console.log(`[DashboardDataService] ${widgetType} API Response:`, result);
        setData(result.data);
        setMetadata(result.metadata || {});
      }
    } catch (err: any) {
      console.error(`Error in useDashboardData for ${widgetType}:`, err);
      setError(err.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [activeTenancy.id, widgetType]);
  
  useEffect(() => {
    fetchData();
    
    // Set up polling every 30 seconds
    // const pollInterval = setInterval(fetchData, 30000);
    
    // return () => clearInterval(pollInterval);
  }, [fetchData]);
  
  return { data, isLoading, error, metadata, refetch: fetchData };
};
